import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { LANG, isAdminPath } from "@/lib/i18n/lang";

const AuthContext = createContext(null);

function readImpersonator() {
  try {
    const raw = window.localStorage.getItem("gcw_impersonator");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function AuthProvider({ children }) {
  // three states: null = checking, User = authed, false = unauthed
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonator, setImpersonator] = useState(() => (typeof window !== "undefined" ? readImpersonator() : null));

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || !user.id || isAdminPath(window.location.pathname) || user.preferred_language === LANG) return;
    api.put("/i18n/me/language", { lang: LANG }).then(() => setUser((u) => (u ? { ...u, preferred_language: LANG } : u))).catch(() => {});
  }, [user]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check —
    // AuthCallback will handle exchanging session_id and then set the user.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password, remember_me = false, two_factor_code = undefined) => {
    const { data } = await api.post("/auth/login", { email, password, remember_me, two_factor_code });
    if (data.session_token) {
      // Fallback for environments without cross-site cookies (e.g. sandbox iframes)
      window.localStorage.setItem("gcw_bearer", data.session_token);
    }
    setUser(data.user);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.session_token) {
      window.localStorage.setItem("gcw_bearer", data.session_token);
    }
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    window.localStorage.removeItem("gcw_bearer");
    window.localStorage.removeItem("gcw_admin_bearer");
    window.localStorage.removeItem("gcw_impersonator");
    setImpersonator(null);
    setUser(false);
  };

  const setActiveMode = async (mode) => {
    const { data } = await api.post("/auth/mode-switch", { mode });
    setUser(data);
    return data;
  };

  const applyUser = (u, session_token) => {
    if (session_token) window.localStorage.setItem("gcw_bearer", session_token);
    setUser(u);
  };

  // ---------------- Admin impersonation ("act as user") ----------------
  const startImpersonation = async (userId) => {
    const adminToken = window.localStorage.getItem("gcw_bearer") || "";
    const { data } = await api.post(`/auth/impersonate/${userId}`);
    window.localStorage.setItem("gcw_admin_bearer", adminToken);
    if (data.impersonator) window.localStorage.setItem("gcw_impersonator", JSON.stringify(data.impersonator));
    if (data.session_token) window.localStorage.setItem("gcw_bearer", data.session_token);
    setImpersonator(data.impersonator || null);
    setUser(data.user);
    return data;
  };

  const stopImpersonation = async () => {
    const adminToken = window.localStorage.getItem("gcw_admin_bearer") || "";
    const { data } = await api.post("/auth/impersonate-stop", { admin_token: adminToken });
    if (data.session_token) window.localStorage.setItem("gcw_bearer", data.session_token);
    window.localStorage.removeItem("gcw_admin_bearer");
    window.localStorage.removeItem("gcw_impersonator");
    setImpersonator(null);
    setUser(data.user);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, impersonator, login, register, logout, refresh, setActiveMode, applyUser, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
